<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="860"
    persistent
    scrollable
  >
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header pa-4">
        <v-icon color="white" class="mr-2">mdi-lightbulb-on-outline</v-icon>
        <span>Pauta Inteligente</span>
        <v-spacer></v-spacer>
        <v-btn icon dark class="close-btn" :disabled="loading" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-stepper v-model="step" flat class="pauta-stepper">
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1">Período</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2">Temas</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 3" step="3">Formatos</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="4">Conteúdo</v-stepper-step>
        </v-stepper-header>

        <!-- Sem max-height: o corpo fica dentro do v-stepper (não é filho
             direto do v-card), então o stepper vira coluna flex e só este
             v-card-text rola — o diálogo `scrollable` limita a 90% da tela. -->
        <v-card-text class="ds-dialog-body pauta-body">
          <v-stepper-items>
            <!-- Etapa 1 — Seleção de período -->
            <v-stepper-content step="1" class="pa-0">
              <section class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-calendar-range</v-icon>
                    <span>Período</span>
                  </div>
                </div>
                <div class="text-body-2 grey--text text--darken-1 mb-3">
                  Escolha o período das consultas a analisar.
                </div>

                <v-btn-toggle
                  v-model="periodMode"
                  mandatory
                  color="primary"
                  class="mb-4 flex-wrap"
                >
                  <v-btn value="semana" small>Última semana</v-btn>
                  <v-btn value="quinze" small>Últimos 15 dias</v-btn>
                  <v-btn value="custom" small>Personalizado</v-btn>
                </v-btn-toggle>

                <v-row v-if="periodMode === 'custom'" dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="dataInicio"
                      type="date"
                      label="Início"
                      :max="today"
                      dense
                      outlined
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="dataFim"
                      type="date"
                      label="Fim"
                      :max="today"
                      dense
                      outlined
                      hide-details="auto"
                    />
                  </v-col>
                </v-row>

                <div class="text-caption grey--text mt-3">
                  <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
                  <template v-if="resolvedPeriod.inicio && resolvedPeriod.fim">
                    De {{ formatBR(resolvedPeriod.inicio) }} a
                    {{ formatBR(resolvedPeriod.fim) }}
                  </template>
                  <template v-else>Informe o intervalo.</template>
                </div>

                <v-alert
                  v-if="periodError"
                  type="warning"
                  dense
                  outlined
                  class="mt-3 mb-0 text-body-2"
                >
                  {{ periodError }}
                </v-alert>
              </section>
            </v-stepper-content>

            <!-- Etapa 2 — Temas -->
            <v-stepper-content step="2" class="pa-0">
              <!-- Carregando -->
              <div
                v-if="loading"
                class="d-flex flex-column align-center justify-center py-10"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="48"
                  width="4"
                />
                <span class="text-body-1 mt-3 primary--text font-weight-medium">
                  Analisando as consultas do período...
                </span>
                <span class="text-caption grey--text mt-1">
                  O pipeline roda vários agentes de IA; pode levar alguns segundos.
                </span>
              </div>

              <!-- Erro -->
              <v-alert
                v-else-if="errorMessage"
                type="error"
                dense
                outlined
                class="mb-0"
              >
                <div class="text-body-2">{{ errorMessage }}</div>
              </v-alert>

              <!-- Vazio -->
              <v-alert
                v-else-if="emptyMessage"
                type="info"
                dense
                outlined
                class="mb-0"
              >
                {{ emptyMessage }}
              </v-alert>

              <!-- Resultado -->
              <div v-else-if="response">
                <div class="d-flex align-center flex-wrap mb-3">
                  <v-chip small color="primary" outlined class="mr-2 mb-1">
                    <v-icon left small>mdi-calendar-range</v-icon>
                    {{ response.analyzed_period || "Período não informado" }}
                  </v-chip>
                  <v-chip small color="indigo" outlined class="mb-1">
                    <v-icon left small>mdi-tag-multiple-outline</v-icon>
                    {{ response.total_theme_groups }} temas sugeridos
                  </v-chip>
                </div>

                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-text-box-outline</v-icon>
                      <span>Resumo geral</span>
                    </div>
                  </div>
                  <div class="text-body-2" style="white-space: pre-wrap">
                    {{ response.summary }}
                  </div>
                </section>

                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-tag-multiple-outline</v-icon>
                      <span>Temas</span>
                    </div>
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-2">
                    Selecione o tema que vai alimentar a próxima etapa:
                  </div>

                  <v-card
                    v-for="(group, i) in response.theme_groups"
                    :key="i"
                    outlined
                    class="mb-3 pa-3 theme-card"
                    :class="{ 'theme-card--selected': selectedThemeIndex === i }"
                    @click="selectTheme(i)"
                  >
                    <div class="d-flex align-start">
                      <v-icon
                        small
                        class="mr-2 mt-1"
                        :color="selectedThemeIndex === i ? 'primary' : 'grey lighten-1'"
                      >
                        {{
                          selectedThemeIndex === i
                            ? "mdi-check-circle"
                            : "mdi-checkbox-blank-circle-outline"
                        }}
                      </v-icon>
                      <div class="flex-grow-1">
                        <div class="text-subtitle-1 font-weight-medium primary--text">
                          {{ group.unified_theme }}
                        </div>
                      </div>
                      <v-chip
                        small
                        :color="adherenceColor(group.adherence_level)"
                        text-color="white"
                        class="ml-2"
                      >
                        {{ group.adherence_level }} · {{ group.score }}
                      </v-chip>
                    </div>

                    <div class="text-body-2 mt-2">{{ group.description }}</div>

                    <div class="text-caption grey--text mt-2">
                      <v-icon x-small class="mr-1">mdi-lightbulb-outline</v-icon>
                      {{ group.relevance_justification }}
                    </div>

                    <div
                      v-if="group.source_themes && group.source_themes.length"
                      class="mt-2"
                    >
                      <v-chip
                        v-for="(src, j) in group.source_themes"
                        :key="j"
                        x-small
                        outlined
                        class="mr-1 mb-1"
                      >
                        {{ src }}
                      </v-chip>
                    </div>
                  </v-card>
                </section>
              </div>
            </v-stepper-content>

            <!-- Etapa 5 — Formatos -->
            <v-stepper-content step="3" class="pa-0">
              <section class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-tag-outline</v-icon>
                    <span>Tema selecionado</span>
                  </div>
                </div>
                <div class="text-subtitle-1 font-weight-medium primary--text">
                  {{ selectedTheme ? selectedTheme.unified_theme : "—" }}
                </div>
                <div v-if="selectedTheme" class="text-body-2 mt-1">
                  {{ selectedTheme.description }}
                </div>
              </section>

              <!-- Carregando -->
              <div
                v-if="formatsLoading"
                class="d-flex flex-column align-center justify-center py-8"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="42"
                  width="4"
                />
                <span class="text-body-2 mt-3 primary--text font-weight-medium">
                  Sugerindo formatos de conteúdo...
                </span>
              </div>

              <!-- Erro -->
              <v-alert
                v-else-if="formatsError"
                type="error"
                dense
                outlined
                class="mt-3 mb-0"
              >
                <div class="text-body-2">{{ formatsError }}</div>
              </v-alert>

              <!-- Resultado -->
              <section v-else-if="formatsResponse" class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-shape-outline</v-icon>
                    <span>Formatos</span>
                  </div>
                </div>
                <div
                  v-if="formatsResponse.summary"
                  class="text-body-2 grey--text text--darken-1 mb-3"
                  style="white-space: pre-wrap"
                >
                  {{ formatsResponse.summary }}
                </div>

                <div class="text-body-2 grey--text text--darken-1 mb-2">
                  Selecione o formato que melhor encaixa na sua produção:
                </div>

                <v-card
                  v-for="(fmt, i) in formatsResponse.formats"
                  :key="i"
                  outlined
                  class="mb-3 pa-3 theme-card"
                  :class="{ 'theme-card--selected': selectedFormatIndex === i }"
                  @click="selectFormat(i)"
                >
                  <div class="d-flex align-start">
                    <v-icon
                      small
                      class="mr-2 mt-1"
                      :color="selectedFormatIndex === i ? 'primary' : 'grey lighten-1'"
                    >
                      {{
                        selectedFormatIndex === i
                          ? "mdi-check-circle"
                          : "mdi-checkbox-blank-circle-outline"
                      }}
                    </v-icon>
                    <div class="flex-grow-1">
                      <div class="text-subtitle-1 font-weight-medium primary--text">
                        {{ fmt.format_type }}
                      </div>
                    </div>
                    <v-chip small color="indigo" text-color="white" class="ml-2">
                      {{ fmt.score }}
                    </v-chip>
                  </div>

                  <div class="text-body-2 mt-2">
                    <span class="font-weight-medium">Ângulo:</span>
                    {{ fmt.editorial_angle }}
                  </div>
                  <div class="text-body-2 mt-1">
                    <span class="font-weight-medium">CTA:</span>
                    {{ fmt.suggested_cta }}
                  </div>
                  <div v-if="fmt.rationale" class="text-caption grey--text mt-2">
                    <v-icon x-small class="mr-1">mdi-lightbulb-outline</v-icon>
                    {{ fmt.rationale }}
                  </div>
                </v-card>
              </section>
            </v-stepper-content>

            <!-- Etapa 6 — Conteúdo -->
            <v-stepper-content step="4" class="pa-0">
              <!-- Carregando -->
              <div
                v-if="contentLoading"
                class="d-flex flex-column align-center justify-center py-8"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="42"
                  width="4"
                />
                <span class="text-body-2 mt-3 primary--text font-weight-medium">
                  Gerando a peça de conteúdo...
                </span>
              </div>

              <!-- Erro -->
              <v-alert
                v-else-if="contentError"
                type="error"
                dense
                outlined
                class="mb-0"
              >
                <div class="text-body-2">{{ contentError }}</div>
              </v-alert>

              <!-- Resultado -->
              <div v-else-if="contentResponse">
                <div class="d-flex align-center flex-wrap mb-3">
                  <v-chip small color="primary" outlined class="mr-2 mb-1">
                    {{ contentResponse.format_type }}
                  </v-chip>
                  <v-chip small color="indigo" outlined class="mb-1">
                    {{ contentResponse.theme }}
                  </v-chip>
                </div>

                <!-- Gancho -->
                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-hook</v-icon>
                      <span>Gancho</span>
                    </div>
                    <v-btn
                      icon
                      x-small
                      title="Copiar gancho"
                      @click="copiar(contentResponse.hook, 'Gancho')"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-body-2" style="white-space: pre-wrap">
                    {{ contentResponse.hook }}
                  </div>
                </section>

                <!-- Blocos (slides/seções) -->
                <section v-for="(bloco, i) in contentResponse.blocks" :key="i" class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-text-box-outline</v-icon>
                      <span>{{ bloco.label }}</span>
                    </div>
                    <v-btn
                      icon
                      x-small
                      :title="`Copiar ${bloco.label}`"
                      @click="copiar(bloco.text, bloco.label)"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-body-2" style="white-space: pre-wrap">
                    {{ bloco.text }}
                  </div>
                </section>

                <!-- Legenda -->
                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-comment-text-outline</v-icon>
                      <span>Legenda</span>
                    </div>
                    <v-btn
                      icon
                      x-small
                      title="Copiar legenda"
                      @click="copiar(contentResponse.caption, 'Legenda')"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-body-2" style="white-space: pre-wrap">
                    {{ contentResponse.caption }}
                  </div>
                </section>

                <!-- CTA -->
                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-bullhorn-outline</v-icon>
                      <span>CTA</span>
                    </div>
                    <v-btn
                      icon
                      x-small
                      title="Copiar CTA"
                      @click="copiar(contentResponse.cta, 'CTA')"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-body-2" style="white-space: pre-wrap">
                    {{ contentResponse.cta }}
                  </div>
                </section>

                <!-- Hashtags -->
                <section v-if="contentResponse.hashtags && contentResponse.hashtags.length" class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-pound</v-icon>
                      <span>Hashtags</span>
                    </div>
                    <v-btn
                      icon
                      x-small
                      title="Copiar hashtags"
                      @click="copiar(contentResponse.hashtags.join(' '), 'Hashtags')"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <v-chip
                    v-for="(tag, j) in contentResponse.hashtags"
                    :key="j"
                    x-small
                    outlined
                    class="mr-1 mb-1"
                  >
                    {{ tag }}
                  </v-chip>
                </section>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-card-text>
      </v-stepper>

      <v-card-actions class="ds-dialog-actions">
        <v-btn
          v-if="step > 1"
          text
          :disabled="loading || formatsLoading || contentLoading"
          @click="voltar"
        >
          <v-icon left small>mdi-chevron-left</v-icon>
          Voltar
        </v-btn>
        <v-spacer></v-spacer>

        <!-- Etapa 1 -->
        <v-btn
          v-if="step === 1"
          color="primary"
          depressed
          :disabled="!!periodError || !resolvedPeriod.inicio"
          @click="generate"
        >
          Gerar pauta
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>

        <!-- Etapa 2 -->
        <v-btn
          v-if="step === 2 && (errorMessage || emptyMessage) && !loading"
          color="primary"
          outlined
          @click="generate"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Tentar novamente
        </v-btn>
        <v-btn
          v-if="step === 2 && response && !loading"
          color="primary"
          depressed
          :disabled="selectedThemeIndex === null"
          @click="goToFormatos"
        >
          Continuar
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>

        <!-- Etapa 3 (Formatos) -->
        <v-btn
          v-if="step === 3 && formatsError && !formatsLoading"
          color="primary"
          outlined
          @click="loadFormatos"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Tentar novamente
        </v-btn>
        <v-btn
          v-if="step === 3 && formatsResponse && !formatsLoading"
          color="primary"
          depressed
          :disabled="selectedFormatIndex === null"
          @click="goToConteudo"
        >
          Continuar
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>

        <!-- Etapa 4 (Conteúdo) -->
        <v-btn
          v-if="step === 4 && contentError && !contentLoading"
          color="primary"
          outlined
          @click="loadConteudo"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Tentar novamente
        </v-btn>
        <v-btn
          v-if="step === 4 && contentResponse && !contentLoading"
          color="primary"
          depressed
          @click="copiarTudo"
        >
          <v-icon left small>mdi-content-copy</v-icon>
          Copiar tudo
        </v-btn>

        <v-btn
          text
          :disabled="loading || formatsLoading || contentLoading"
          @click="close"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  gerarPautaInteligente,
  sugerirFormatos,
  gerarConteudo,
} from "@/services/pautaInteligenteService";

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default {
  name: "PautaInteligenteDialog",
  props: {
    value: { type: Boolean, default: false },
    doctorId: { type: [Number, String], default: null },
  },
  setup() {
    const { error, success } = useSnackbar();
    return { snackError: error, snackSuccess: success };
  },
  data() {
    return {
      step: 1,
      periodMode: "semana",
      dataInicio: "",
      dataFim: "",
      loading: false,
      response: null,
      errorMessage: "",
      emptyMessage: "",
      selectedThemeIndex: null,
      formatsLoading: false,
      formatsResponse: null,
      formatsError: "",
      selectedFormatIndex: null,
      contentLoading: false,
      contentResponse: null,
      contentError: "",
    };
  },
  computed: {
    today() {
      return toISODate(new Date());
    },
    resolvedPeriod() {
      const hoje = new Date();
      const fim = toISODate(hoje);
      if (this.periodMode === "semana") {
        const ini = new Date(hoje);
        ini.setDate(ini.getDate() - 7);
        return { inicio: toISODate(ini), fim };
      }
      if (this.periodMode === "quinze") {
        const ini = new Date(hoje);
        ini.setDate(ini.getDate() - 15);
        return { inicio: toISODate(ini), fim };
      }
      return { inicio: this.dataInicio, fim: this.dataFim };
    },
    periodError() {
      const { inicio, fim } = this.resolvedPeriod;
      if (!inicio || !fim) return "Informe as datas de início e fim.";
      const iniD = new Date(`${inicio}T00:00:00`);
      const fimD = new Date(`${fim}T00:00:00`);
      const hojeD = new Date(`${this.today}T00:00:00`);
      if (fimD < iniD) return "A data de fim deve ser igual ou posterior à de início.";
      if (fimD > hojeD) return "A data de fim não pode ser no futuro.";
      const limite = new Date(iniD);
      limite.setMonth(limite.getMonth() + 1);
      if (fimD > limite) return "O intervalo não pode ser maior que 1 mês.";
      return "";
    },
    selectedTheme() {
      if (this.selectedThemeIndex === null || !this.response) return null;
      return this.response.theme_groups[this.selectedThemeIndex] || null;
    },
    selectedFormat() {
      if (this.selectedFormatIndex === null || !this.formatsResponse) return null;
      return this.formatsResponse.formats[this.selectedFormatIndex] || null;
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    formatBR(isoDate) {
      // Converte "YYYY-MM-DD" (formato do toISODate/resolvedPeriod) em "DD/MM/YYYY".
      if (!isoDate) return "";
      const [y, m, d] = String(isoDate).split("-");
      if (!y || !m || !d) return isoDate;
      return `${d}/${m}/${y}`;
    },
    reset() {
      this.step = 1;
      this.periodMode = "semana";
      const hoje = new Date();
      const ini = new Date(hoje);
      ini.setDate(ini.getDate() - 7);
      this.dataInicio = toISODate(ini);
      this.dataFim = toISODate(hoje);
      this.response = null;
      this.errorMessage = "";
      this.emptyMessage = "";
      this.selectedThemeIndex = null;
      this.loading = false;
      this.resetFormatos();
    },
    resetFormatos() {
      this.formatsLoading = false;
      this.formatsResponse = null;
      this.formatsError = "";
      this.selectedFormatIndex = null;
      this.resetConteudo();
    },
    resetConteudo() {
      this.contentLoading = false;
      this.contentResponse = null;
      this.contentError = "";
    },
    async generate() {
      if (!this.doctorId) {
        this.step = 2;
        this.errorMessage = "Médico não identificado.";
        return;
      }
      if (this.periodError) return;

      this.step = 2;
      this.loading = true;
      this.errorMessage = "";
      this.emptyMessage = "";
      this.response = null;
      this.selectedThemeIndex = null;
      try {
        const res = await gerarPautaInteligente(this.doctorId, {
          dataInicio: this.resolvedPeriod.inicio,
          dataFim: this.resolvedPeriod.fim,
        });
        if (res.success) {
          this.response = res.data;
        } else {
          this.emptyMessage =
            res.message || "Nenhuma consulta encontrada no período selecionado.";
        }
      } catch (err) {
        const detail =
          err.response?.data?.message ||
          err.response?.data?.detail ||
          err.message ||
          "Falha ao gerar a pauta.";
        this.errorMessage = detail;
        this.snackError("Erro ao gerar a Pauta Inteligente");
      } finally {
        this.loading = false;
      }
    },
    selectTheme(i) {
      this.selectedThemeIndex = i;
    },
    goToFormatos() {
      if (this.selectedThemeIndex === null) return;
      this.step = 3;
      this.loadFormatos();
    },
    async loadFormatos() {
      if (!this.selectedTheme) return;
      this.resetFormatos();
      this.formatsLoading = true;
      try {
        const res = await sugerirFormatos(this.selectedTheme);
        if (res.success) {
          this.formatsResponse = res.data;
        } else {
          this.formatsError =
            res.message || "Não foi possível sugerir os formatos.";
        }
      } catch (err) {
        const detail =
          err.response?.data?.message ||
          err.response?.data?.detail ||
          err.message ||
          "Falha ao sugerir formatos.";
        this.formatsError = detail;
        this.snackError("Erro ao sugerir os formatos de conteúdo");
      } finally {
        this.formatsLoading = false;
      }
    },
    selectFormat(i) {
      this.selectedFormatIndex = i;
    },
    goToConteudo() {
      if (this.selectedFormatIndex === null) return;
      this.step = 4;
      this.loadConteudo();
    },
    async loadConteudo() {
      if (!this.selectedTheme || !this.selectedFormat) return;
      this.resetConteudo();
      this.contentLoading = true;
      try {
        const res = await gerarConteudo(this.selectedTheme, this.selectedFormat);
        if (res.success) {
          this.contentResponse = res.data;
        } else {
          this.contentError = res.message || "Não foi possível gerar o conteúdo.";
        }
      } catch (err) {
        const detail =
          err.response?.data?.message ||
          err.response?.data?.detail ||
          err.message ||
          "Falha ao gerar o conteúdo.";
        this.contentError = detail;
        this.snackError("Erro ao gerar a peça de conteúdo");
      } finally {
        this.contentLoading = false;
      }
    },
    async copiar(texto, rotulo) {
      try {
        await navigator.clipboard.writeText(texto || "");
        this.snackSuccess(`${rotulo} copiado`);
      } catch (e) {
        this.snackError("Não foi possível copiar");
      }
    },
    copiarTudo() {
      const c = this.contentResponse;
      if (!c) return;
      const partes = [
        c.hook,
        ...(c.blocks || []).map((b) => `${b.label}\n${b.text}`),
        c.caption,
        c.cta,
        (c.hashtags || []).join(" "),
      ].filter((p) => p && p.trim());
      this.copiar(partes.join("\n\n"), "Conteúdo completo");
    },
    voltar() {
      if (this.step > 1) this.step -= 1;
    },
    adherenceColor(level) {
      const l = (level || "").toLowerCase();
      if (l.startsWith("alta")) return "success";
      if (l.startsWith("m")) return "warning"; // media / média
      if (l.startsWith("baixa")) return "grey";
      return "blue-grey";
    },
    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.pauta-stepper {
  box-shadow: none;
  /* Coluna flex: o cabeçalho de etapas fica fixo e só o corpo rola. */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
/* O corpo não é filho direto do v-card, então a regra global do
   .ds-dialog-body não o alcança: o recuo e a rolagem vêm daqui. */
.pauta-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  background-color: var(--ds-blue-bg-soft);
}
.theme-card {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.theme-card:hover {
  border-color: var(--v-primary-base);
}
.theme-card--selected {
  border-color: var(--v-primary-base);
  background-color: rgba(31, 77, 63, 0.06);
}
</style>
